import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { DeliveryLeadTime } from '@1po/1po-bff-fe-spec/generated/backoffice/delivery_lead_time/response/DeliveryLeadTimeResponse';
import {
  ReferenceStock,
  Warehouse,
  WarehouseStockDetail,
} from '@1po/1po-bff-fe-spec/generated/catalog/trading_data/model/ReferenceStock';
import { DMSReferenceStock, DMSWarehouseStockDetail } from '@1po/1po-bff-fe-spec/generated/dms/model/DMSReferenceStock';
import { DealerType } from '@1po/1po-bff-fe-spec/generated/user/model/CommercialLink';
import { TFunction } from 'i18next';
import moment from 'moment';
import { RootState } from 'app/AppStore';
import { Ban, ExadisStoreIcon, ExclamationTriangleIcon, TruckIcon, WarehouseIcon } from 'assets/icons';
import { ReactComponent as GlobeIcon } from 'assets/icons/globe.svg';
import { ReactComponent as HourglassHalfIcon } from 'assets/icons/hourglass-half.svg';
import { ReactComponent as IndustryIcon } from 'assets/icons/industry.svg';
import { getBulkOilDeliveryTime } from 'components/StockInfo/deliveryTimeUtils';
import { OneBox, ShortStockDescription } from 'components/StockInfo/StockDisplay.styled';
import { useIsOilChunkUserR1 } from 'domains/appContext/AppContext.requests';
import { getDeliveryLeadTime } from 'domains/deliveryLeadTime/DeliveryLeadTime.store';
import { getDealerType, getUserContext, getUserCountry } from 'domains/user';
import { ThemeDisplayStyleKeyType } from 'styles';
import { displayStyle } from 'styles/displayStyle';
import { Box, Flex, Icon, MarginBox, Text, WithTooltip } from 'UI';
import { getData, NO_DATA, textFormatter } from 'utils';

const STOCK_DELIVERY_DATA_CY = 'stock-delivery-text';

export const isPviOnlyCentralWarehouse = (isPviOnlyReference = false, warehouseType: Warehouse = 'INVALID') =>
  isPviOnlyReference && warehouseType === 'CENTRAL';

interface StockTextsI {
  warehouse: string;
  description: string[];
}

const _MS_PER_DAY = 1000 * 60 * 60 * 24;

export const getDaysUntilDelivered = (expectedDeliveryDate: string | undefined, relativeDate: Date): number | null => {
  if (!expectedDeliveryDate) {
    return null;
  }
  const deliveryDate = new Date(expectedDeliveryDate);
  if (!moment(new Date(deliveryDate)).isValid()) {
    return null;
  }
  const utcDelivery = Date.UTC(deliveryDate.getFullYear(), deliveryDate.getMonth(), deliveryDate.getDate());
  const utcToCompare = Date.UTC(relativeDate.getFullYear(), relativeDate.getMonth(), relativeDate.getDate());

  return Math.floor((utcDelivery - utcToCompare) / _MS_PER_DAY);
};

const getExadisDisplayStyle = (
  expectedDeliveryDate: string | undefined,
  relativeDate: Date,
  depan = false,
  isExadisPickUp = false,
  stock?: WarehouseStockDetail,
  basketCount?: number,
) => {
  const daysUntilDelivered = getDaysUntilDelivered(expectedDeliveryDate, relativeDate);
  if (isExadisPickUp) {
    return daysUntilDelivered !== null &&
      daysUntilDelivered <= 1 &&
      stock &&
      basketCount &&
      basketCount <= stock.confirmedQuantity
      ? 'info'
      : 'error';
  }
  if (depan) {
    return 'viewed_link';
  }
  return daysUntilDelivered !== null && daysUntilDelivered > 2 ? 'viewed_link' : 'success';
};

const getMarketplaceDisplayStyle = (stock?: WarehouseStockDetail): ThemeDisplayStyleKeyType => {
  if (stock?.confirmedQuantity === 0) {
    return 'error';
  }
  return stock?.primary ? 'green_dark' : 'link';
};

export const getDisplayStyle = (
  type: Warehouse,
  expectedDeliveryDate: string | undefined,
  depan: boolean | undefined,
  relativeDate: Date,
  userCountry: string | undefined,
  isExadisPickUp = false,
  isCanOil = false,
  isDrumOil = false,
  isBulkOil = false,
  isOilUserR1 = false,
  stock?: WarehouseStockDetail,
  basketCount?: number,
  isPviOnlyReference = false,
): ThemeDisplayStyleKeyType => {
  if (isPviOnlyCentralWarehouse(isPviOnlyReference, type)) {
    return 'error';
  }
  if (isBulkOil || (isOilUserR1 && (isCanOil || isDrumOil))) {
    return 'stocks_purple';
  }
  switch (type) {
    case 'CENTRAL':
      return 'info';
    case 'COUNTRY':
      return 'info';
    case 'REGION':
      return userCountry === 'IT' || userCountry === 'ES' ? 'green_dark' : 'info';
    case 'LOCAL':
    case 'MANUFACTURER': {
      const daysUntilDelivered = getDaysUntilDelivered(expectedDeliveryDate, relativeDate);
      if (daysUntilDelivered === null) {
        return 'success';
      }
      return daysUntilDelivered <= 1 ? 'info' : 'warning';
    }
    case 'WW':
      return 'viewed_link';
    case 'EXADIS': {
      return getExadisDisplayStyle(expectedDeliveryDate, relativeDate, depan, isExadisPickUp, stock, basketCount);
    }
    case 'MARKETPLACE': {
      return getMarketplaceDisplayStyle(stock);
    }
    case 'INVALID':
    case 'OTHER':
    case 'DELAYED':
    default:
      if (isBulkOil || isDrumOil) {
        return 'stocks_purple';
      } else {
        return 'warning';
      }
  }
};

export const getIcon = (
  centered: boolean,
  type: Warehouse,
  expectedDeliveryDate: string | undefined,
  depan: boolean | undefined,
  relativeDate: Date,
  userCountry: string | undefined,
  isExadisPickUp = false,
  isCanOil = false,
  isDrumOil = false,
  isBulkOil = false,
  isOilUserR1 = false,
  size?: number | undefined,
  stock?: WarehouseStockDetail,
  basketCount?: number,
  isPviOnlyReference = false,
) => {
  const getIconComponent = (type: Warehouse, expectedDeliveryDate: string | undefined) => {
    if (isPviOnlyCentralWarehouse(isPviOnlyReference, type)) {
      return ExclamationTriangleIcon;
    }
    if (isBulkOil || (isOilUserR1 && (isCanOil || isDrumOil))) {
      return GlobeIcon;
    }
    switch (type) {
      case 'LOCAL':
        return TruckIcon;
      case 'CENTRAL':
      case 'REGION':
      case 'COUNTRY':
      case 'MANUFACTURER':
        return IndustryIcon;
      case 'WW':
        return GlobeIcon;
      case 'EXADIS': {
        if (isExadisPickUp) {
          return ExadisStoreIcon;
        }
        const daysUntilDelivered = getDaysUntilDelivered(expectedDeliveryDate, relativeDate);

        if (daysUntilDelivered === null && !depan) {
          return Ban;
        }
        return TruckIcon;
      }
      case 'MARKETPLACE': {
        if (stock?.confirmedQuantity === 0) {
          return Ban;
        }
        return TruckIcon;
      }
      case 'INVALID':
      case 'DELAYED':
      default:
        if (isBulkOil || isDrumOil) {
          return GlobeIcon;
        } else {
          return HourglassHalfIcon;
        }
    }
  };

  return (
    <Icon
      IconComponent={getIconComponent(type, expectedDeliveryDate)}
      size={size ?? 20}
      mr={centered ? 0 : 5}
      color={
        displayStyle[
          getDisplayStyle(
            type,
            expectedDeliveryDate,
            depan,
            relativeDate,
            userCountry,
            isExadisPickUp,
            isCanOil,
            isDrumOil,
            isBulkOil,
            isOilUserR1,
            stock,
            basketCount,
            isPviOnlyReference,
          )
        ].color
      }
      display={'block'}
      data-cy={'stock-icon'}
      noPointer
    />
  );
};

function getStockDescription(basketCount: number, emptyBasketDescription: string, description: string) {
  const isBasketEmpty = basketCount === 0;
  return isBasketEmpty ? [emptyBasketDescription] : [description];
}

function getDelayedStockTexts(
  t: TFunction,
  confirmedQuantityStockDescription: string,
  relativeDate: Date,
  isCanOil: boolean,
  isDrumOil: boolean,
  isBulkOil: boolean,
  isOilUserR1: boolean,
): StockTextsI {
  if (isBulkOil) {
    return {
      warehouse: getBulkOilDeliveryTime(t, relativeDate),
      description: [],
    };
  }
  if (isDrumOil || (isOilUserR1 && isCanOil)) {
    return {
      warehouse: t(
        'catalog.reference.stock.out_of_stock.part_unavailable_within_six_business_days',
        'Within 6 business days',
      ),
      description: [],
    };
  }
  return {
    warehouse: t('catalog.reference.stock.restocking', 'Restocking:'),
    description: [confirmedQuantityStockDescription],
  };
}

function getExadisStockTexts(
  t: TFunction,
  expectedDeliveryDate: string | undefined,
  relativeDate: Date,
  basketCount: number,
  isExadisPickUp: boolean,
  confirmedQuantity: number,
  depan = false,
): StockTextsI {
  const daysUntilDelivered = getDaysUntilDelivered(expectedDeliveryDate, relativeDate);
  if ((daysUntilDelivered === null && !depan) || basketCount === 0) {
    return {
      warehouse: '',
      description: [''],
    };
  }
  if (isExadisPickUp) {
    return {
      warehouse: t('catalog.reference.stock.exadis_pickup_delivery', '[{{count}}] units in stock at Exadis pick-up', {
        count: confirmedQuantity,
      }),
      description: [''],
    };
  }
  return {
    warehouse: t('catalog.reference.stock.IAM_delivery_pieces', '{{count}} pieces:', {
      count: confirmedQuantity,
    }),
    description: [''],
  };
}

export function getStockTexts(
  t: TFunction,
  stock: WarehouseStockDetail,
  basketCount: number,
  relativeDate: Date,
  depan: boolean | undefined,
  userCountry: string | undefined,
  isCanOil = false,
  isDrumOil = false,
  isBulkOil = false,
  isOilUserR1 = false,
  isExadisPickUp = false,
  isPviOnlyReference = false,
): StockTextsI {
  const { type, availableQuantity, confirmedQuantity, expectedDeliveryDate, deliveryInformation } = stock;

  if (isPviOnlyCentralWarehouse(isPviOnlyReference, type)) {
    return {
      warehouse: t('catalog.reference.stock.pvi_only', 'Only available for PVI'),
      description: [],
    };
  }

  const deliveryInformationArr = deliveryInformation?.split('|').map((inf) => inf.trim());
  const deliveryInformationOneLine = deliveryInformationArr?.[0];
  const deliveryInformationTheRest = deliveryInformationArr?.slice(1);
  const confirmedQuantityStockDescription = t('catalog.reference.stock.units', '{{count}} units', {
    count: confirmedQuantity,
  });
  if (isBulkOil || (isOilUserR1 && (isCanOil || isDrumOil))) {
    return {
      warehouse: t(
        'catalog.reference.stock.out_of_stock.part_unavailable_within_six_business_days',
        'Within 6 business days',
      ),
      description: [],
    };
  }
  switch (type) {
    case 'LOCAL':
      return {
        warehouse: t('catalog.reference.stock.r1', 'R1:'),
        description: getStockDescription(
          basketCount,
          t('catalog.reference.stock.r1.units', '{{count}} units in stock at the R1 warehouse', {
            count: availableQuantity,
          }),
          t(
            'catalog.reference.stock.r1.description_with_basket_count',
            '{{basketCount}}/{{count}} unit(s) in stock at the R1 warehouse',
            {
              basketCount: confirmedQuantity,
              count: availableQuantity,
            },
          ),
        ),
      };
    case 'REGION':
      return {
        warehouse: t('catalog.reference.stock.regional', 'Regional warehouse:'),
        description: getStockDescription(
          basketCount,
          t('catalog.reference.stock.regional.description', 'In stock at the Regional warehouse'),
          confirmedQuantityStockDescription,
        ),
      };
    case 'CENTRAL':
      if (isOilUserR1 && isBulkOil) {
        return {
          warehouse: t(
            'catalog.reference.stock.out_of_stock.part_unavailable_within_six_business_days',
            'Within 6 business days',
          ),
          description: [],
        };
      }
      return {
        warehouse: t('catalog.reference.stock.central', 'Central warehouse:'),
        description: getStockDescription(
          basketCount,
          t('catalog.reference.stock.central.description', 'In stock at the Central warehouse'),
          confirmedQuantityStockDescription,
        ),
      };
    case 'COUNTRY':
      return {
        warehouse: t('catalog.reference.stock.country', 'Country warehouse:'),
        description: getStockDescription(
          basketCount,
          t('catalog.reference.stock.country.description', 'In stock at the Country warehouse'),
          confirmedQuantityStockDescription,
        ),
      };
    case 'MANUFACTURER': {
      const manufacturerDeliveryText = expectedDeliveryDate
        ? textFormatter.formatDateShort(new Date(expectedDeliveryDate))
        : undefined;
      return {
        warehouse: t('catalog.reference.stock.manufacturer', 'Manufacturer:'),
        description: getStockDescription(
          basketCount,
          t('catalog.reference.stock.manufacturer.in_stock', 'In stock, will be delivered by {{date}}', {
            date: manufacturerDeliveryText,
          }),
          t('catalog.reference.stock.manufacturer.units', '{{count}} units will be delivered by {{date}}', {
            count: confirmedQuantity,
            date: manufacturerDeliveryText,
          }),
        ),
      };
    }
    case 'OTHER':
      return {
        warehouse: t('catalog.reference.stock.other_location', 'Other:'),
        description: [
          t(
            'catalog.reference.stock.other_location.more_information',
            'Please, contact your dealer for more information',
          ),
        ],
      };
    case 'WW':
      return {
        warehouse: t('catalog.reference.stock.worldwide', 'Worldwide warehouse:'),
        description: getStockDescription(
          basketCount,
          t('catalog.reference.stock.worldwide.description', 'In stock at the Worldwide warehouse'),
          confirmedQuantityStockDescription,
        ),
      };
    case 'DELAYED':
      return getDelayedStockTexts(
        t,
        confirmedQuantityStockDescription,
        relativeDate,
        isCanOil,
        isDrumOil,
        isBulkOil,
        isOilUserR1,
      );
    case 'INVALID':
      return {
        warehouse: t('catalog.reference.stock.stock_noinfo', 'Stock information unavailable.'),
        description: [''],
      };
    case 'EXADIS':
      return getExadisStockTexts(
        t,
        expectedDeliveryDate,
        relativeDate,
        basketCount,
        isExadisPickUp,
        confirmedQuantity,
        depan,
      );
    case 'MARKETPLACE': {
      if (confirmedQuantity === 0) {
        return {
          warehouse: '',
          description: [t('catalog.reference.stock.stock_texts.out_of_stock', 'Out of stock.')],
        };
      }
      if (basketCount === 0) {
        return {
          warehouse: '',
          description: [
            t(
              'catalog.reference.stock.delivery_information_with_available_quantity',
              '[{{count}}] unit(s) in stock, will be delivered by {{deliveryInformation}}',
              {
                deliveryInformation: deliveryInformationOneLine,
                count: availableQuantity,
              },
            ),
            ...(deliveryInformationTheRest ?? []),
          ],
        };
      }
      return {
        warehouse: t('catalog.reference.stock.IAM_delivery_pieces', '{{count}} pieces:', {
          count: confirmedQuantity,
        }),
        description: [
          t(
            'catalog.reference.stock.delivery_information_with_ordered_quantity',
            'will be delivered by {{deliveryInformation}}',
            {
              deliveryInformation: deliveryInformationOneLine,
            },
          ),
          ...(deliveryInformationTheRest?.map((r) => {
            return t(
              'catalog.reference.stock.delivery_information_with_ordered_quantity',
              'will be delivered by {{deliveryInformation}}',
              {
                deliveryInformation: r,
              },
            );
          }) ?? []),
        ],
      };
    }
    default:
      return {
        warehouse: '',
        description: [''],
      };
  }
}

export const getDeliveryText = (
  t: TFunction,
  type: Warehouse,
  leadTime: DeliveryLeadTime | undefined,
  expectedDeliveryDate: string | undefined,
  depan: boolean | undefined,
  dealerType: DealerType | undefined,
  relativeDate: Date,
  isExadisPickUp = false,
  isCanOil = false,
  isDrumOil = false,
  isBulkOil = false,
  isOilUserR1 = false,
) => {
  if (isBulkOil || (isOilUserR1 && (isCanOil || isDrumOil))) {
    return '';
  }
  switch (type) {
    case 'REGION':
    case 'COUNTRY':
    case 'CENTRAL': {
      if (!leadTime || leadTime.isDisabled || !leadTime.from || !leadTime.to || (isOilUserR1 && isBulkOil)) {
        return '';
      }
      const time =
        leadTime.time === 'HOURS'
          ? t('catalog.reference.stock.usually_delivery_part2_hours', ' hours')
          : t('catalog.reference.stock.usually_delivery_part2_day', ' days');
      return (
        t('catalog.reference.stock.usually_delivery_part1', ', usually delivered within {{from}} to {{to}}', {
          from: leadTime.from,
          to: leadTime.to,
        }) + time
      );
    }
    case 'DELAYED': {
      return '';
    }
    case 'EXADIS': {
      if (isExadisPickUp) {
        return '';
      }
      if (depan) {
        return handleExadisDepan(dealerType, t);
      }

      const daysUntilDelivered = getDaysUntilDelivered(expectedDeliveryDate, relativeDate);

      if (daysUntilDelivered === null) {
        return t('catalog.reference.stock.IAM_delivery_unknown', 'Delivery time unknown');
      }

      const deliveryDate = new Date(expectedDeliveryDate as string);
      const cutoffHours = textFormatter.formatDateHourMinute(deliveryDate);

      return handleExadisNonDepan(deliveryDate, dealerType, t, cutoffHours);
    }
    default:
      return '';
  }
};

function handleExadisDepan(dealerType: string | undefined, t: TFunction) {
  return getDeliveryTextByDealer(
    dealerType,
    t('catalog.reference.stock.IAM_special_order_delivery', 'Special order (>72h)'),
    t('catalog.reference.stock.IAM_special_order_delivery_to_R1', 'Special order (>72h at your R1)'),
  );
}

function handleExadisNonDepan(deliveryDate: Date, dealerType: string | undefined, t: TFunction, cutoffHours: string) {
  const dateFormatted = textFormatter.formatDateLongNoYear(deliveryDate);
  return getDeliveryTextByDealer(
    dealerType,
    t('catalog.reference.stock.IAM_delivery', 'will be delivered on {{date}} before {{cutoffHours}}', {
      date: dateFormatted,
      cutoffHours,
    }),
    t('catalog.reference.stock.IAM_delivery_to_R1', 'will be delivered to your R1 on {{date}} before {{cutoffHours}}', {
      date: dateFormatted,
      cutoffHours,
    }),
  );
}

const getDeliveryTextByDealer = (dealerType: string | undefined, r1Text: string, nonR1Text: string) => {
  if (dealerType === 'R1') {
    return r1Text;
  }
  return nonR1Text;
};

export interface DescriptionI {
  type: Warehouse;
  description: string[];
  deliveryText: string;
  displayStyle?: ThemeDisplayStyleKeyType | ThemeDisplayStyleKeyType[] | false;
}

function BaseDescription({ description, deliveryText, displayStyle }: DescriptionI) {
  return (
    <>
      {description.length > 1 ? (
        <Flex direction={'column'}>
          {description.map((desc) => (
            <Text key={desc} type="base" color="grey35" displayStyle={displayStyle} data-cy={STOCK_DELIVERY_DATA_CY}>
              {description}
            </Text>
          ))}
          <Text type="base" color="grey35" displayStyle={displayStyle} data-cy={STOCK_DELIVERY_DATA_CY}>
            {deliveryText}
          </Text>
        </Flex>
      ) : (
        <Text type="base" color="grey35" displayStyle={displayStyle} data-cy={STOCK_DELIVERY_DATA_CY}>
          {description}
          {deliveryText}
        </Text>
      )}
    </>
  );
}

function NarrowDescription({ description, deliveryText, displayStyle }: DescriptionI) {
  return (
    <ShortStockDescription maxWidth={145}>
      <WithTooltip title={`${description}${deliveryText}`}>
        {description.length > 1 ? (
          <OneBox>
            {description.map((description, i) => (
              <Text
                ellipsis
                key={i}
                type="base"
                color="grey35"
                displayStyle={displayStyle}
                data-cy={STOCK_DELIVERY_DATA_CY}
              >
                {description}
              </Text>
            ))}
            <Text ellipsis type="base" color="grey35" displayStyle={displayStyle} data-cy={STOCK_DELIVERY_DATA_CY}>
              {deliveryText}
            </Text>
          </OneBox>
        ) : (
          <Text ellipsis type="base" color="grey35" displayStyle={displayStyle} data-cy={STOCK_DELIVERY_DATA_CY}>
            {description}
            {deliveryText}
          </Text>
        )}
      </WithTooltip>
    </ShortStockDescription>
  );
}

export interface StockTextProps {
  stock: ReferenceStock | NO_DATA;
  quantity?: number | undefined;
  narrow?: boolean;
  displayIcon?: boolean;
  displayIndex: number;
  warehouse: WarehouseStockDetail;
  isCanOil?: boolean;
  isDrumOil?: boolean;
  isBulkOil?: boolean;
  isCertasOilFamily?: boolean;
  isExadisPickUp?: boolean;
  isPviOnlyReference?: boolean;
}

export function StockText({
  stock,
  quantity,
  narrow,
  displayIcon,
  displayIndex,
  warehouse,
  isCanOil,
  isDrumOil,
  isBulkOil,
  isCertasOilFamily,
  isExadisPickUp,
  isPviOnlyReference,
}: Readonly<StockTextProps>) {
  const deliveryLeadTime = useSelector((state: RootState) => getDeliveryLeadTime(state, warehouse.type));

  return (
    <StockTextWithDeliveryType
      stock={stock}
      quantity={quantity}
      narrow={narrow}
      deliveryLeadTime={deliveryLeadTime}
      displayIcon={displayIcon}
      displayIndex={displayIndex}
      warehouse={warehouse}
      isCanOil={isCanOil}
      isDrumOil={isDrumOil}
      isBulkOil={isBulkOil}
      isCertasOilFamily={isCertasOilFamily}
      isExadisPickUp={isExadisPickUp}
      isPviOnlyReference={isPviOnlyReference}
    />
  );
}

export interface StockTextWithDeliveryTypeProps {
  stock: ReferenceStock | NO_DATA;
  quantity?: number;
  warehouse: WarehouseStockDetail;
  narrow?: boolean;
  deliveryLeadTime: DeliveryLeadTime | undefined;
  displayIcon?: boolean;
  displayIndex: number;
  dealerType?: DealerType | undefined;
  r1Country?: string;
  // date to which we count the stock delivery time
  relativeDate?: Date;
  isCanOil?: boolean;
  isDrumOil?: boolean;
  isBulkOil?: boolean;
  isCertasOilFamily?: boolean;
  isExadisPickUp?: boolean;
  isPviOnlyReference?: boolean;
}

function shouldHideStockText(
  isOilUserR1: boolean,
  isCanOil: boolean,
  isDrumOil: boolean,
  displayIndex: number,
  currentStock: WarehouseStockDetail,
): boolean {
  const isOilR1CanOrDrum = isOilUserR1 && (isCanOil || isDrumOil);
  return isOilR1CanOrDrum
    ? displayIndex > 0
    : currentStock.confirmedQuantity === 0 && currentStock.stockist !== 'MARKETPLACE';
}

export function StockTextWithDeliveryType({
  stock,
  quantity,
  warehouse,
  narrow,
  deliveryLeadTime,
  displayIcon,
  displayIndex,
  relativeDate = new Date(),
  isCanOil = false,
  isDrumOil = false,
  isBulkOil = false,
  isExadisPickUp = false,
  isPviOnlyReference = false,
}: Readonly<StockTextWithDeliveryTypeProps>) {
  const { t } = useTranslation();
  const stockData = getData(stock);
  const dealerType = useSelector(getDealerType);
  const { r1Country } = useSelector(getUserContext);
  const userCountry = useSelector(getUserCountry);
  const isOilUserR1 = useIsOilChunkUserR1();

  if (!stockData) {
    return null;
  }
  const currentStock = warehouse;
  if (shouldHideStockText(isOilUserR1, isCanOil, isDrumOil, displayIndex, currentStock)) {
    return null;
  }
  const texts = getStockTexts(
    t,
    currentStock,
    quantity ?? 0,
    relativeDate,
    currentStock.depan,
    userCountry,
    isCanOil,
    isDrumOil,
    isBulkOil,
    isOilUserR1,
    isExadisPickUp,
    isPviOnlyReference,
  );
  const deliveryText = getDeliveryText(
    t,
    currentStock.type,
    deliveryLeadTime,
    currentStock.expectedDeliveryDate,
    currentStock.depan,
    dealerType,
    relativeDate,
    isExadisPickUp,
    isCanOil,
    isDrumOil,
    isBulkOil,
    isOilUserR1,
  );
  const showStockForExadis = currentStock.type !== 'EXADIS' || r1Country === 'FR';
  const Description: FunctionComponent<DescriptionI> = narrow ? NarrowDescription : BaseDescription;

  const establishmentNameAndWarehouseDisplay = stockData.sellerName
    ? `${stockData.sellerName} ${texts.warehouse}`
    : texts.warehouse;

  const isBulkOilFR = isBulkOil && userCountry === 'FR';

  const displayStocksText = isBulkOilFR
    ? getBulkOilDeliveryTime(t, relativeDate)
    : establishmentNameAndWarehouseDisplay;

  return (
    <>
      {showStockForExadis && (
        <Flex align={'flex-start'} data-cy={'stocks-delivery'} overflowX={'hidden'}>
          {displayIcon &&
            getIcon(
              false,
              currentStock.type,
              currentStock.expectedDeliveryDate,
              currentStock.depan,
              relativeDate,
              userCountry,
              isExadisPickUp,
              isCanOil,
              isDrumOil,
              isBulkOil,
              isOilUserR1,
              undefined,
              currentStock,
              quantity ?? 0,
              isPviOnlyReference,
            )}
          <Box maxWidth={20}>
            <Text
              type="base"
              bold
              color="grey35"
              displayStyle={getDisplayStyle(
                currentStock.type,
                currentStock.expectedDeliveryDate,
                currentStock.depan,
                relativeDate,
                userCountry,
                isExadisPickUp,
                isCanOil,
                isDrumOil,
                isBulkOil,
                isOilUserR1,
                currentStock,
                quantity ?? 0,
                isPviOnlyReference,
              )}
              noWrap
              ellipsis
              data-cy={'stock-warehouse'}
            >
              {displayStocksText}
            </Text>
          </Box>
          <MarginBox ml={5} />
          {currentStock.location && (
            <Box maxWidth={20}>
              <Text
                type="base"
                bold
                color="grey35"
                displayStyle={getDisplayStyle(
                  currentStock.type,
                  currentStock.expectedDeliveryDate,
                  currentStock.depan,
                  relativeDate,
                  userCountry,
                  isExadisPickUp,
                  isCanOil,
                  isDrumOil,
                  isBulkOil,
                  isOilUserR1,
                  currentStock,
                  quantity ?? 0,
                )}
                noWrap
                ellipsis
                data-cy={'stock-warehouse'}
              >
                [{currentStock.location}]
              </Text>
            </Box>
          )}
          <MarginBox ml={5} />
          {!isBulkOilFR && (
            <Description type={currentStock.type} description={texts.description} deliveryText={deliveryText} />
          )}
        </Flex>
      )}
    </>
  );
}

export interface DMSStockTextProps {
  stock: DMSReferenceStock | NO_DATA;
  warehouse: DMSWarehouseStockDetail;
  size?: number;
}

function getDMSStockTextByAvailableValues(t: TFunction, warehouse: DMSWarehouseStockDetail) {
  switch (true) {
    case !!warehouse.siteLabel && !!warehouse.location:
      return {
        stockText: t(
          'catalog.reference.stock.dms.quantity-site-location',
          '{{quantity}} unit(s) in {{siteLabel}} ({{location}})',
          {
            quantity: warehouse.quantity,
            siteLabel: warehouse.siteLabel,
            location: warehouse.location,
          },
        ),
      };
    case !!warehouse.siteLabel:
      return {
        stockText: t('catalog.reference.stock.dms.quantity-site', '{{quantity}} unit(s) in {{siteLabel}}', {
          quantity: warehouse.quantity,
          siteLabel: warehouse.siteLabel,
        }),
      };
    case !!warehouse.location:
      return {
        stockText: t('catalog.reference.stock.dms.quantity-location', '{{quantity}} unit(s) in {{location}}', {
          quantity: warehouse.quantity,
          location: warehouse.location,
        }),
      };
    default:
      return {
        stockText: t('catalog.reference.stock.dms.quantity', '{{quantity}} unit(s)', {
          quantity: warehouse.quantity,
        }),
      };
  }
}

export function DMSStockText({ stock, warehouse, size }: Readonly<DMSStockTextProps>) {
  const { t } = useTranslation();

  if (stock === undefined || warehouse.quantity === null || warehouse.quantity === 0) {
    return <></>;
  }

  const { stockText } = getDMSStockTextByAvailableValues(t, warehouse);

  return (
    <Flex align={'flex-start'}>
      <Icon
        IconComponent={WarehouseIcon}
        size={size ?? 20}
        mr={5}
        color={'stocks_pink'}
        display={'block'}
        data-cy={'dms_stock-icon'}
        noPointer
      />
      <Box maxWidth={20}>
        <Text
          type="base"
          bold
          color="grey35"
          displayStyle={'stocks_pink'}
          noWrap
          ellipsis
          data-cy={'dms-stock-warehouse'}
        >
          {t('catalog.reference.stock.dms.name', 'My Stock:')}
        </Text>
      </Box>
      <MarginBox ml={5} />
      <MarginBox ml={5} />
      <Flex direction={'column'}>
        <Text type="base" color="grey35" data-cy={'dms-stock-description-text'}>
          {stockText}
        </Text>
      </Flex>
    </Flex>
  );
}
